import React, {useEffect, useState} from 'react';
import Modal from '../workspace/Modal';

export interface AdminWordlistCreateSubmission {
  name: string;
  description: string;
  words: string[];
  isPublic: boolean;
  isOfficial: boolean;
  userId: string;
}

interface AdminWordlistFormModalProps {
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (submission: AdminWordlistCreateSubmission) => void;
}

const controlClass = 'w-full rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2 text-sm '
  + 'text-gray-100 placeholder-gray-600 focus:border-indigo-500 focus:outline-none '
  + 'focus:ring-1 focus:ring-indigo-500 disabled:opacity-50';

/**
 * Admin-only creation. A plain newline-per-word textarea rather than the one-at-a-time builder
 * `WordListFormModal` uses — official catalog lists are pasted in from an existing source far more
 * often than typed word by word.
 *
 * `userId` targets any owner and defaults to blank, which the thunk sends as `undefined` so the
 * server falls back to the calling admin — spelled out here so an operator doesn't have to know
 * their own internal id to create a list for themselves.
 */
const AdminWordlistFormModal: React.FC<AdminWordlistFormModalProps> = ({
  isOpen,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [wordsText, setWordsText] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isOfficial, setIsOfficial] = useState(false);
  const [userId, setUserId] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setName('');
    setDescription('');
    setWordsText('');
    setIsPublic(false);
    setIsOfficial(false);
    setUserId('');
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const canSave = name.trim().length > 0 && !isSaving;

  const submit = () => {
    if (!canSave) {
      return;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      words: wordsText.split('\n').map(word => word.trim()).filter(word => word.length > 0),
      isPublic,
      isOfficial,
      userId: userId.trim(),
    });
  };

  return (
    <Modal
      isOpen
      title="New word list"
      subtitle="Catalog management — this doesn't add the list to anyone's own collection."
      onClose={isSaving ? () => {} : onClose}
      widthClass="max-w-xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-100
             transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
             hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed
             cursor-pointer"
          >
            {isSaving ? 'Creating…' : 'Create list'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="admin-wordlist-name" className="mb-1 block text-xs font-medium text-gray-300">
            Name
          </label>
          <input
            id="admin-wordlist-name"
            type="text"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="Common profanity"
            disabled={isSaving}
            className={controlClass}
          />
        </div>

        <div>
          <label
            htmlFor="admin-wordlist-description"
            className="mb-1 block text-xs font-medium text-gray-300"
          >
            Description <span className="font-normal text-gray-600">(optional)</span>
          </label>
          <textarea
            id="admin-wordlist-description"
            value={description}
            onChange={event => setDescription(event.target.value)}
            placeholder="What this list is for"
            rows={2}
            disabled={isSaving}
            className={`${controlClass} resize-none`}
          />
        </div>

        <div>
          <label htmlFor="admin-wordlist-owner" className="mb-1 block text-xs font-medium text-gray-300">
            Owner user id <span className="font-normal text-gray-600">(optional — defaults to you)</span>
          </label>
          <input
            id="admin-wordlist-owner"
            type="text"
            value={userId}
            onChange={event => setUserId(event.target.value)}
            placeholder="public.users.id"
            disabled={isSaving}
            className={`${controlClass} font-mono`}
          />
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-300">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={event => setIsPublic(event.target.checked)}
              disabled={isSaving}
              className="h-3.5 w-3.5 accent-indigo-500"
            />
            Public — listed in the marketplace
          </label>
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-300">
            <input
              type="checkbox"
              checked={isOfficial}
              onChange={event => setIsOfficial(event.target.checked)}
              disabled={isSaving}
              className="h-3.5 w-3.5 accent-indigo-500"
            />
            Official
          </label>
        </div>

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <label htmlFor="admin-wordlist-words" className="text-xs font-medium text-gray-300">
              Words <span className="font-normal text-gray-600">(one per line)</span>
            </label>
            <span className="text-xs tabular-nums text-gray-600">
              {wordsText.split('\n').map(w => w.trim()).filter(w => w.length > 0).length} words
            </span>
          </div>
          <textarea
            id="admin-wordlist-words"
            value={wordsText}
            onChange={event => setWordsText(event.target.value)}
            placeholder={'word one\nword two\nword three'}
            rows={8}
            disabled={isSaving}
            className={`${controlClass} resize-y font-mono`}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AdminWordlistFormModal;
